export interface FaqItem {
  question: string;
  answer: string;
}

export const FAQ_ITEMS: readonly FaqItem[] = [
  {
    question: 'What is PHP2Shell?',
    answer:
      'PHP2Shell is the public name for two critical PHP vulnerabilities disclosed on 21 October 2026: CVE-2026-109618 and CVE-2026-109619. They affect PHP installations that serve web requests. Upstream fixes shipped in PHP 8.2.35, 8.3.36, 8.4.27, and 8.5.12.',
  },
  {
    question: 'Which PHP releases contain the upstream PHP2Shell fix?',
    answer:
      'The PHP project fixed PHP2Shell in PHP 8.2.35, 8.3.36, 8.4.27, and 8.5.12. PHP 8.1 and earlier are end of life and did not receive those upstream fixes. A hosting provider may also ship its own backport.',
  },
  {
    question: 'Can this PHP2Shell scanner prove a website is patched?',
    answer:
      'No. The scanner only reads publicly visible HTTP headers. A visible version can be hidden, rewritten, or different from the runtime that serves the site. Confirm the web server PHP build on the server or with the hosting provider.',
  },
  {
    question: 'Does the scanner exploit PHP2Shell?',
    answer:
      'No. It sends one ordinary GET request to the public homepage after the destination passes safety checks. It does not send exploit payloads or test the vulnerability itself.',
  },
] as const;

export interface SeoPage {
  id: 'home' | 'privacy' | 'terms' | 'not-found';
  path: string;
  title: string;
  description: string;
  robots: string;
  includeFaq: boolean;
}

export const SEO_PAGES = {
  home: {
    id: 'home',
    path: '/',
    title: 'PHP2Shell Scanner — Check a Website for PHP2Shell',
    description:
      'Non-invasive PHP2Shell scanner for CVE-2026-109618 and CVE-2026-109619. It reads public PHP version headers and does not prove a server is patched.',
    robots: 'index, follow',
    includeFaq: true,
  },
  privacy: {
    id: 'privacy',
    path: '/privacy',
    title: 'Privacy Information — PHP2Shell Scanner',
    description:
      'How PHP2Shell Scanner handles a submitted website URL, the local check log, and Google AdSense advertising cookies.',
    robots: 'index, follow',
    includeFaq: false,
  },
  terms: {
    id: 'terms',
    path: '/terms',
    title: 'Terms of Use — PHP2Shell Scanner',
    description:
      'Use PHP2Shell Scanner only on websites you own or are authorized to assess. Reports are not a warranty that a server is vulnerable or patched.',
    robots: 'index, follow',
    includeFaq: false,
  },
  notFound: {
    id: 'not-found',
    path: '/404',
    title: 'Page Not Found — PHP2Shell Scanner',
    description: 'That address is not part of PHP2Shell Scanner.',
    robots: 'noindex, follow',
    includeFaq: false,
  },
} as const satisfies Record<string, SeoPage>;

export function pageForPathname(pathname: string): SeoPage {
  const withoutQuery = pathname.split('?')[0] ?? '/';
  const normalized =
    withoutQuery.length > 1 && withoutQuery.endsWith('/') ? withoutQuery.slice(0, -1) : withoutQuery;
  if (normalized === '/' || normalized === '') return SEO_PAGES.home;
  if (normalized === '/privacy') return SEO_PAGES.privacy;
  if (normalized === '/terms') return SEO_PAGES.terms;
  return SEO_PAGES.notFound;
}

export function canonicalUrl(origin: string, page: SeoPage, requestPath?: string): string {
  const base = origin.replace(/\/+$/, '');
  if (page.id === 'home') return `${base}/`;
  if (page.id === 'not-found') {
    const path = requestPath && /^\/[A-Za-z0-9/_-]{1,200}$/.test(requestPath) ? requestPath : '/';
    return `${base}${path}`;
  }
  return `${base}${page.path}`;
}

export function structuredData(page: SeoPage, pageUrl: string, siteOrigin: string): Record<string, unknown> {
  const origin = siteOrigin.replace(/\/+$/, '');
  const website = {
    '@type': 'WebSite',
    name: 'PHP2Shell Scanner',
    url: `${origin}/`,
    description: SEO_PAGES.home.description,
  };
  const pageNode =
    page.id === 'home'
      ? {
          '@type': 'WebApplication',
          name: 'PHP2Shell Scanner',
          url: pageUrl,
          description: page.description,
          applicationCategory: 'SecurityApplication',
          operatingSystem: 'Any',
          isAccessibleForFree: true,
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
        }
      : {
          '@type': 'WebPage',
          name: page.title,
          url: pageUrl,
          description: page.description,
        };

  const graph: Record<string, unknown>[] = [website, pageNode];
  if (page.includeFaq) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

export function renderRobotsTxt(origin: string): string {
  const base = origin.replace(/\/+$/, '');
  return `User-agent: *\nAllow: /\nDisallow: /api/\n\nSitemap: ${base}/sitemap.xml\n`;
}

export function renderSitemapXml(origin: string): string {
  const pages = [SEO_PAGES.home, SEO_PAGES.privacy, SEO_PAGES.terms];
  const urls = pages
    .map((page) => {
      const loc = escapeXml(canonicalUrl(origin, page));
      return `  <url><loc>${loc}</loc><lastmod>2026-10-24</lastmod></url>`;
    })
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
